import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { User } from './types';
export type { User } from './types'; // backward compatibility
import { AuthContext } from './AuthContextInternal';
import {
  consumeLogoutNavigationPurgeProof,
  type LogoutNavigationPurgeProof,
} from './logoutBoundary';
import { isOfflineCacheEnabled, warmRoleCaches } from '../pwa/offlineCacheClient';
import { authKeys } from '../queries/auth/authKeys';
import { useAuthBootstrapQuery, useRefreshAuthMutation } from '../queries/auth/useAuthQueries';
import { isBootstrapRateLimitError, resetBootstrap } from './authBootstrap';
import { purgeNavigationState } from '../navigation/sessionHooks';
import { advanceAuthFetchPrincipalEpoch } from '../utils/authFetch';

// NOTE: User & AuthState interfaces now live in types.ts for fast-refresh friendliness.

// Context & value interface imported from AuthContextInternal.


interface AuthProviderProps { initialUser?: User | null; suppressBootstrap?: boolean; children: React.ReactNode }

interface AuthRefreshFlight {
  generation: number;
  promise: Promise<User | null>;
}

function principalFingerprint(user: User | null): string {
  return [
    user?.id ?? 'anonymous',
    String(user?.role ?? '').toLowerCase(),
    user?.is_developer ? 1 : 0,
    user?.can_manage_reception_all_branches === true ? 1 : 0,
    user?.simulation?.active ? 1 : 0,
    user?.simulation?.role ?? '',
    user?.simulation?.target_employee_id ?? '',
    user?.simulation?.target_student_id ?? '',
    user?.simulation?.effects_policy ?? '',
  ].join(':');
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ initialUser = null, suppressBootstrap = false, children }) => {
  const queryClient = useQueryClient();
  const [manualUser, setManualUser] = useState<User | null | undefined>(undefined);
  const [authEpoch, setAuthEpoch] = useState(0);
  const authEpochRef = useRef(0);
  const pendingAuthQueryPublicationRef = useRef<{ epoch: number; user: User | null } | null>(null);
  const lastWarmedRoleRef = useRef<string | null>(null);
  const normalRefreshInFlightRef = useRef<AuthRefreshFlight | null>(null);
  const forcedRefreshInFlightRef = useRef<AuthRefreshFlight | null>(null);
  const refreshGenerationRef = useRef(0);
  const logoutCascadeRef = useRef(false);

  const bootstrapEnabled = !initialUser && !suppressBootstrap;
  const bootstrapQuery = useAuthBootstrapQuery({
    enabled: bootstrapEnabled,
    initialUser,
  });
  const refreshMutation = useRefreshAuthMutation();
  const runRefreshMutation = refreshMutation.mutateAsync;

  // Keep external API stable: manual user updates still override query state.
  const user = useMemo(
    () => (manualUser !== undefined ? manualUser : (bootstrapQuery.data ?? initialUser ?? null)),
    [manualUser, bootstrapQuery.data, initialUser]
  );
  const bootstrapRateLimited = isBootstrapRateLimitError(bootstrapQuery.error);

  const loading = refreshMutation.isPending || (bootstrapEnabled && bootstrapQuery.isFetching);
  const bootstrapResolved = suppressBootstrap
    || Boolean(initialUser)
    || manualUser !== undefined
    || bootstrapQuery.isFetched
    || (bootstrapQuery.isError && !bootstrapRateLimited)
    || refreshMutation.isSuccess;

  const currentUserRef = useRef<User | null>(user);
  const stalePrincipalQueriesRef = useRef<Set<object> | null>(null);
  const principalMutationObserversRef = useRef(new Set<{ reset: () => void }>());
  currentUserRef.current = user;

  // Insertion effects run before every child layout effect. That ordering is
  // required because a child may start a mutation and rotate the principal in
  // its first layout effect; subscribing in a parent layout effect is too late.
  React.useInsertionEffect(() => {
    const observers = principalMutationObserversRef.current;
    const unsubscribe = queryClient.getMutationCache().subscribe((event) => {
      if (event.type === 'observerAdded' || event.type === 'observerOptionsUpdated') {
        observers.add(event.observer);
      } else if (event.type === 'observerRemoved') {
        observers.delete(event.observer);
      }
    });
    return () => {
      unsubscribe();
      observers.clear();
    };
  }, [queryClient]);

  const publishReplacement = useCallback((
    nextUser: User | null,
    reason: 'logout' | 'scope-change',
    navigationAlreadyPurged = false,
  ) => {
    advanceAuthFetchPrincipalEpoch();
    if (!navigationAlreadyPurged) purgeNavigationState(reason);
    const nextEpoch = authEpochRef.current + 1;
    authEpochRef.current = nextEpoch;
    pendingAuthQueryPublicationRef.current = { epoch: nextEpoch, user: nextUser };
    const queryCache = queryClient.getQueryCache();
    const staleQueries = queryCache.getAll().filter((query) => {
      const key = query.queryKey;
      return !(key.length === 2 && key[0] === 'auth' && key[1] === 'bootstrap');
    });
    const pendingStaleQueries = stalePrincipalQueriesRef.current ?? new Set<object>();
    for (const query of staleQueries) pendingStaleQueries.add(query);
    stalePrincipalQueriesRef.current = pendingStaleQueries;
    const mutationCache = queryClient.getMutationCache();
    // MutationCache.clear() only forgets entries; a pending Mutation still owns
    // its callbacks and can otherwise write principal A's result into B's cache.
    // Retire both hook-level callbacks and per-call observer callbacks before
    // publishing the replacement identity.
    for (const mutation of mutationCache.getAll()) {
      mutation.setOptions({
        ...mutation.options,
        onMutate: undefined,
        onSuccess: undefined,
        onError: undefined,
        onSettled: undefined,
      });
    }
    for (const observer of Array.from(principalMutationObserversRef.current)) {
      observer.reset();
    }
    principalMutationObserversRef.current.clear();
    mutationCache.clear();
    setAuthEpoch(nextEpoch);
    setManualUser(nextUser);
    currentUserRef.current = nextUser;
    // Removing an observed query makes its still-mounted observer immediately
    // recreate and refetch it. Reset observed entries in place so no old data
    // survives and defer their physical removal until the replacement render
    // has disabled/re-scoped those observers. Inactive entries can go now.
    for (const query of staleQueries) {
      query.reset();
      if (query.state.data !== undefined || query.state.error !== null) {
        query.setState({
          data: undefined,
          dataUpdatedAt: 0,
          error: null,
          errorUpdatedAt: 0,
          fetchStatus: 'idle',
          status: 'pending',
        });
      }
      if (query.getObserversCount() === 0) queryCache.remove(query);
    }
    if (reason === 'logout') resetBootstrap();
  }, [queryClient]);

  React.useLayoutEffect(() => {
    const staleQueries = stalePrincipalQueriesRef.current;
    if (staleQueries) {
      stalePrincipalQueriesRef.current = null;
      queryClient.removeQueries({ predicate: (query) => staleQueries.has(query) });
    }
    const pending = pendingAuthQueryPublicationRef.current;
    if (!pending || pending.epoch !== authEpoch || pending.user !== manualUser) return;
    pendingAuthQueryPublicationRef.current = null;
    queryClient.setQueryData(authKeys.bootstrap(), pending.user);
  }, [authEpoch, manualUser, queryClient]);

  const setUser = useCallback((nextUser: User | null) => {
    if (nextUser === null) {
      if (logoutCascadeRef.current) return;
      logoutCascadeRef.current = true;
      refreshGenerationRef.current += 1;
      publishReplacement(null, 'logout');
      return;
    }
    logoutCascadeRef.current = false;
    resetBootstrap();
    refreshGenerationRef.current += 1;
    publishReplacement(nextUser, 'scope-change');
  }, [publishReplacement]);

  const clearAuth = useCallback((proof?: LogoutNavigationPurgeProof) => {
    const navigationAlreadyPurged = consumeLogoutNavigationPurgeProof(proof);
    if (logoutCascadeRef.current) return;
    logoutCascadeRef.current = true;
    refreshGenerationRef.current += 1;
    publishReplacement(null, 'logout', navigationAlreadyPurged);
  }, [publishReplacement]);

  // A saved name does not replace the principal or retire other page drafts.
  const updatePersonalProfile = useCallback((profile: { id: number; full_name: string }) => {
    const current = currentUserRef.current;
    if (!current || current.id !== profile.id || current.role === 'student' || current.simulation?.active) return;
    const next = { ...current, full_name: profile.full_name };
    currentUserRef.current = next;
    setManualUser(next);
    queryClient.setQueryData(authKeys.bootstrap(), next);
  }, [queryClient]);

  const refreshAuth = useCallback((opts?: { force?: boolean }): Promise<User | null> => {
    const force = Boolean(opts?.force);
    const publishRefreshResult = (nextUser: User | null, generation: number) => {
      if (generation !== refreshGenerationRef.current) return false;
      const previous = currentUserRef.current;
      const principalChanged = principalFingerprint(previous) !== principalFingerprint(nextUser);
      if (force || principalChanged) {
        logoutCascadeRef.current = nextUser === null;
        publishReplacement(nextUser, nextUser === null ? 'logout' : 'scope-change');
      } else {
        setManualUser(nextUser);
        currentUserRef.current = nextUser;
        queryClient.setQueryData(authKeys.bootstrap(), nextUser);
      }
      return true;
    };

    if (force) {
      const activeForced = forcedRefreshInFlightRef.current;
      if (activeForced?.generation === refreshGenerationRef.current) {
        return activeForced.promise;
      }

      const currentGeneration = refreshGenerationRef.current;
      const pendingNormalFlight = normalRefreshInFlightRef.current;
      const pendingNormal = pendingNormalFlight?.generation === currentGeneration
        ? pendingNormalFlight.promise
        : null;
      const generation = currentGeneration + 1;
      refreshGenerationRef.current = generation;
      const start = pendingNormal
        ? pendingNormal.then(() => undefined, () => undefined)
        : Promise.resolve();
      const forcedRefresh: Promise<User | null> = start
        .then(() => runRefreshMutation({ force: true }))
        .then((nextUser) => {
          return publishRefreshResult(nextUser, generation) ? nextUser : null;
        })
        .finally(() => {
          if (forcedRefreshInFlightRef.current?.promise === forcedRefresh) {
            forcedRefreshInFlightRef.current = null;
          }
        });
      forcedRefreshInFlightRef.current = { generation, promise: forcedRefresh };
      return forcedRefresh;
    }

    const activeForced = forcedRefreshInFlightRef.current;
    if (activeForced?.generation === refreshGenerationRef.current) return activeForced.promise;
    const activeNormal = normalRefreshInFlightRef.current;
    if (activeNormal?.generation === refreshGenerationRef.current) return activeNormal.promise;

    const generation = refreshGenerationRef.current;
    const normalRefresh: Promise<User | null> = runRefreshMutation({ force: false })
      .then((nextUser) => {
        return publishRefreshResult(nextUser, generation) ? nextUser : null;
      })
      .finally(() => {
        if (normalRefreshInFlightRef.current?.promise === normalRefresh) {
          normalRefreshInFlightRef.current = null;
        }
      });
    normalRefreshInFlightRef.current = { generation, promise: normalRefresh };
    return normalRefresh;
  }, [publishReplacement, queryClient, runRefreshMutation]);

  useEffect(() => {
    // Keep query cache aligned with server-provided user at mount/hydration.
    if (initialUser) {
      queryClient.setQueryData(authKeys.bootstrap(), initialUser);
    }
  }, [initialUser, queryClient]);

  useEffect(() => {
    if (!isOfflineCacheEnabled || loading) return;
    const role = user?.role ? String(user.role).trim().toLowerCase() : '';
    if (!role) {
      lastWarmedRoleRef.current = null;
      return;
    }
    if (lastWarmedRoleRef.current === role) return;
    lastWarmedRoleRef.current = role;
    void warmRoleCaches(role);
  }, [loading, user?.id, user?.role]);

  const contextValue = useMemo(() => ({
    user,
    role: user?.role,
    loading,
    bootstrapResolved,
    authEpoch,
    setUser,
    clearAuth,
    refreshAuth,
    updatePersonalProfile,
  }), [authEpoch, bootstrapResolved, clearAuth, loading, refreshAuth, setUser, updatePersonalProfile, user]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// Hook moved to separate file `useAuth.ts` to keep this file focused on provider export for React Fast Refresh.
